import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, ShieldCheck } from "lucide-react";
import { ASSETS, waLink, scrollToId, WHATSAPP_DEFAULT_MESSAGE } from "../lib/site";

export const Hero = ({ hero = {}, phone }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const title =
    hero.title || "Seguridad y tecnología que protege lo que más te importa.";
  const subtitle =
    hero.subtitle ||
    "GLG Technology diseña, instala, integra y desarrolla soluciones de seguridad, conectividad, automatización y software en Honduras.";
  const ecosystem =
    hero.ecosystem ||
    "CCTV · CONTROL DE ACCESO · ALARMAS · REDES · AUTOMATIZACIÓN · SOFTWARE";
  const chips = ecosystem.split("·").map((s) => s.trim()).filter(Boolean);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="noise relative flex min-h-[100svh] items-center overflow-hidden bg-glg-black"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroBg}
          alt="Centro de monitoreo y videovigilancia"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-glg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-glg-black/50 via-glg-black/70 to-glg-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-glg-black via-glg-black/75 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_40%,rgba(0,71,255,0.18),transparent_45%)]" />
      </motion.div>

      <div className="pointer-events-none absolute -left-40 top-1/4 z-0 h-96 w-96 rounded-full bg-glg-blue/15 blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 z-0 h-80 w-80 rounded-full bg-glg-orange/10 blur-[130px]" />
      <div className="absolute inset-0 z-0 grid-lines opacity-30" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-white/70 backdrop-blur-sm"
        >
          <ShieldCheck className="h-4 w-4 text-glg-green" />
          Empresa tecnológica · Honduras
        </motion.div>

        <div className="max-w-4xl overflow-hidden">
          <motion.h1
            initial={{ y: "18%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-[4.2rem]"
          >
            {title}
          </motion.h1>
        </div>
        <div className="mt-5 h-1 w-24 rounded-full bg-gradient-to-r from-glg-blue via-glg-blue to-glg-orange" />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
        >
          {subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollToId("#contacto")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-glg-blue px-7 py-4 text-sm font-semibold text-white shadow-xl shadow-glg-blue/25 transition-transform duration-200 hover:scale-[1.03]"
          >
            Solicitar cotización
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <a
            href={waLink(WHATSAPP_DEFAULT_MESSAGE, phone)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:border-glg-green hover:bg-glg-green/10"
          >
            <MessageCircle className="h-4 w-4 text-glg-green" />
            Hablar por WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-12 flex flex-wrap gap-x-3 gap-y-2 border-t border-white/10 pt-7"
        >
          {chips.map((c) => (
            <span
              key={c}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-wider text-white/55"
            >
              {c}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
